import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Chip,
    Avatar,
    Typography,
    Divider,
    Button,
    Modal,
    Box,
    CircularProgress,
    Alert,
    IconButton,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import { fetchKnowledgeById } from '../api/knowledgeApi';
import { formatDate } from '../utils/dateUtils';

const KnowledgeCard = ({ id, title, creationDate, user, tags, handleDelete }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);

    const handleOpen = async () => {
        setOpen(true);
        setLoading(true);
        setError(null);
        try {
            const data = await fetchKnowledgeById(id);
            setDetails(data);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setDetails(null);
    };

    return (
        <>
            <Card
                sx={{
                    maxWidth: 345,
                    margin: 'auto',
                    transition: '0.3s',
                    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
                    '&:hover': {
                        boxShadow: '0 16px 70px -12.125px rgba(0,0,0,0.3)',
                    },
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: deepPurple[500] }}>
                            {user.charAt(0).toUpperCase()}
                        </Avatar>
                    }
                    title={title}
                    subheader={`User ID: ${user}`}
                />
                <Divider variant="middle" />
                <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Created on: {formatDate(creationDate)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {tags.map((value) => (
                            <Chip 
                                key={value.id || value} 
                                label={value.classText || value}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                </CardContent>
                <CardActions>
                    <Button size="small" onClick={handleOpen}>
                        Detail
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(id)}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Card>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="knowledge-detail-title"
                aria-describedby="knowledge-detail-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography id="knowledge-detail-title" variant="h6" component="h2">
                            Knowledge Detail
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    ) : details ? (
                        <>
                            <Typography
                                id="knowledge-detail-description"
                                sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                            >
                                {details.knowledgeText}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="textSecondary">
                                Title: {details.knowledgeTitle}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Created on: {formatDate(details.creationDate)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                By: {details.userIdentifier}
                            </Typography>
                        </>
                    ) : null}
                </Box>
            </Modal>
        </>
    );
};

export default KnowledgeCard;
